// File: src/pages/buyer/AddProduct/sections/ShippingTerms.jsx
import React, { useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Row, Col, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { loadGoogleMaps } from "../../../../utils/loadGoogleMaps";

const ShippingTerms = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const incoterm = watch("incoterms");
  const paymentTerm = watch("payment_terms");

  useEffect(() => {
    console.log("[ShippingTerms] WATCH incoterm:", incoterm);
  }, [incoterm]);

  useEffect(() => {
    console.log("[ShippingTerms] payment_terms:", paymentTerm);
  }, [paymentTerm]);

  const deliveryPort = watch("delivery_port");
  useEffect(() => {
    console.log("[ShippingTerms] WATCH delivery_port:", deliveryPort);
  }, [deliveryPort]);

  const INCOTERM_RULES = {
    EXW: { port: false },
    FOB: { port: true },
    DDP: { port: true },
    CIF: { port: true },
    CFR: { port: true },
    DAP: { port: true },
  };

  // Show delivery port if incoterm requires it OR if there's already a value (edit mode)
  // Show delivery port if incoterm requires it OR if there's already a value
  const shouldShowDeliveryPort = INCOTERM_RULES[incoterm]?.port || !!deliveryPort;

  // Initialize Google Places Autocomplete for worldwide ports (skip in edit mode)
  useEffect(() => {
    // if (isEditMode) return; // 👈 Removed: Enable autocomplete in edit mode too
    if (!inputRef.current) return;

    loadGoogleMaps().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ["geocode"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address) {
          setValue("delivery_port", place.formatted_address, {
            shouldValidate: true,
          });
          if (place.geometry?.location) {
            setValue("delivery_latitude", place.geometry.location.lat());
            setValue("delivery_longitude", place.geometry.location.lng());
          }
        }
      });

      autocompleteRef.current = autocomplete;
    });
  }, [setValue, incoterm]);

  // Set input value from form data when component mounts or deliveryPort changes
  useEffect(() => {
    if (inputRef.current) {
      // Only sync if the DOM value is different from the state value to avoid cursor jumps
      if (inputRef.current.value !== (deliveryPort || "")) {
        inputRef.current.value = deliveryPort || "";
      }
    }
  }, [deliveryPort]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (!["FOB", "DDP", "CIF", "CFR", "DAP"].includes(incoterm)) {
      if (incoterm && !isEditMode) { // Only clear if not in edit mode
        setValue("delivery_port", "");
      }
    }
  }, [incoterm, setValue, isEditMode]);

  return (
    <div className="border rounded p-4 mb-4 bg-white">
      <h5 className="fw-semibold mb-4">Shipping & Trade Terms</h5>

      <Row className="g-3 mb-3">
        {/* Incoterms */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Incoterms</Form.Label>
            <Controller
              name="incoterms"
              control={control}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  <option value="FOB">FOB</option>
                  {/* <option value="CIF">CIF</option> */}
                  <option value="EXW">EXW</option>
                  <option value="DDP">DDP</option>
                  {/* <option value="DAP">DAP</option> */}
                </Form.Select>
              )}
            />
            {errors.incoterms && (
              <div className="text-danger small">
                {errors.incoterms.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Payment Terms */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Payment Terms</Form.Label>
            <Controller
              name="payment_terms"
              control={control}
              render={({ field }) => (
                <Form.Select {...field}>
                  <option value="">Select</option>
                  {/* <option value="Advance">Advance</option>
                  <option value="TT / Remittance">TT / Remittance</option>
                  <option value="Net 30">Net 30</option> */}
                  <option value="card">Card</option>
                  <option value="stripe">Stripe</option>
                  {/* <option value="LC">LC</option>
                  <option value="COD">COD</option> */}
                </Form.Select>
              )}
            />
            {errors.payment_terms && (
              <div className="text-danger small">
                {errors.payment_terms.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Lead Time */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Dispatched Lead Time (days)</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter number of days"
              {...register("lead_time")}
              min="1"
              onKeyDown={(e) =>
                ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
              }
            />
            {errors.lead_time && (
              <div className="text-danger small">
                {errors.lead_time.message}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Delivery Port */}
      {shouldShowDeliveryPort && (
        <Row className="mt-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label>Delivery Location / Port</Form.Label>
              <Controller
                name="delivery_port"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    type="text"
                    placeholder="Start typing to search ports worldwide"
                    value={field.value || ""}
                    onChange={field.onChange}
                    ref={(el) => {
                      field.ref(el); // 👈 keep react-hook-form happy
                      inputRef.current = el; // 👈 give Google Autocomplete the DOM node
                    }}
                  />
                )}
              />

              {errors.delivery_port && (
                <div className="text-danger small">
                  {errors.delivery_port.message}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default React.memo(ShippingTerms);
